// src/components/quota.jsx
// QuotaDashboard (My Progress / Team View) and AdminQuotaView modal.

import { useState, useEffect } from "react";
import * as HS from "../hubspot.js";
import { QUOTA_METRICS, DEFAULT_QUOTAS } from "../constants.js";
import { sGet, sSet, qColor, aiCall, serverSave, serverLoad } from "../helpers.js";
import { Btn, Modal, Spin } from "./ui.jsx";

// How much to multiply a "monthly" quota target by for the selected period.
//  - month   -> 1x (use monthly target as-is)
//  - quarter -> 3x (three months in a quarter)
//  - ytd     -> N x, where N = current month number (1=Jan ... 12=Dec)
//
// This means as the year progresses, the YTD goal grows. e.g. on May 1st, YTD
// quota for "Calls Logged" with a monthly target of 80 becomes 5 * 80 = 400.
function periodMultiplier(period) {
  if (period === "quarter") return 3;
  if (period === "ytd")     return new Date().getMonth() + 1;  // 1..12
  return 1;
}

// Scale a quotas object by the period multiplier. Returns a NEW object so the
// underlying monthly quotas (which are what's stored / edited) stay untouched.
function scaleQuotas(monthlyQuotas, period) {
  if (!monthlyQuotas) return null;
  const m = periodMultiplier(period);
  const out = {};
  for (const k of Object.keys(monthlyQuotas)) {
    out[k] = Math.round((monthlyQuotas[k] || 0) * m);
  }
  return out;
}

export function QuotaDashboard({currentUser, users}) {
  const isAdmin = currentUser.isAdmin;
  const [view,    setView]    = useState(isAdmin ? "team" : "mine");
  const [period,  setPeriod]  = useState("month");
  const [quotas,  setQuotas]  = useState(null);
  const [actuals, setActuals] = useState(null);
  const [allActuals, setAllActuals] = useState({});      // active period data shown in UI
  // Tracks which period the current allActuals state represents. When period
  // changes we clear allActuals to avoid showing stale numbers from a
  // different time window.
  const [allActualsPeriod, setAllActualsPeriod] = useState(null);
  const [aiL,     setAiL]     = useState(false);
  const [aiTip,   setAiTip]   = useState("");
  const [editMode, setEditMode] = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [hsLoading, setHsLoading] = useState(false);

  const readActuals = (uid, per) => {
    try {
      const stored = localStorage.getItem("crm_actuals_" + uid + "_" + per);
      const a = stored ? JSON.parse(stored) : {};
      return {calls: a.calls||0, visits: a.visits||0, emails: a.emails||0, quotes: a.quotes||0, sales: a.sales||0};
    } catch { return {calls:0, visits:0, emails:0, quotes:0, sales:0}; }
  };

  useEffect(() => {
    // Load quotas. Prefer server (Azure Blob) so edits on other devices are
    // reflected here. Fall back to local cache if blob is unreachable, then
    // to DEFAULT_QUOTAS for first-time users.
    const localQuotas = sGet("crm_global_quotas") || DEFAULT_QUOTAS;
    setQuotas({...DEFAULT_QUOTAS, ...localQuotas});
    serverLoad("crm_global_quotas").then(serverQuotas => {
      if (serverQuotas && typeof serverQuotas === "object") {
        setQuotas({...DEFAULT_QUOTAS, ...serverQuotas});
        // Keep local cache in sync for offline reads
        sSet("crm_global_quotas", serverQuotas);
      }
    }).catch(()=>{ /* fall back to local cache, already applied above */ });
    const local = readActuals(currentUser.id, period);
    setActuals(local);
    if (isAdmin) {
      // If the period changed since allActuals was last populated, reset it
      // before loading local data. This prevents stale numbers from a different
      // period from lingering.
      setAllActuals(prev => {
        const next = (allActualsPeriod === period) ? {...prev} : {};
        for (const u of (users || []).filter(u => !u.isAdmin)) {
          const local = readActuals(u.id, period);
          const existing = next[u.id] || {};
          // Use max so we don't overwrite real HubSpot numbers (already in
          // `next` if a HubSpot fetch finished before us) with local zeros.
          const merged = {};
          for (const k of Object.keys({...local, ...existing})) {
            merged[k] = Math.max(local[k] || 0, existing[k] || 0);
          }
          next[u.id] = merged;
        }
        return next;
      });
      setAllActualsPeriod(period);
    }
    const hsId = currentUser.hubspotOwnerId ||
      ((currentUser.id || "").startsWith("hs_") ? (currentUser.id || "").slice(3) : null);
    if (hsId && !currentUser.isAdmin) {
      const timer = setTimeout(async () => {
        setHsLoading(true);
        try {
          const [hist, visits, quotes, sales] = await Promise.all([
            HS.getActivityCounts(hsId, period),
            HS.getInPersonVisitCount(hsId, period),
            HS.getDealsCreatedCount(hsId, period),
            HS.getSalesGenerated(hsId, period),
          ]);
          setActuals(prev => ({
            ...prev,
            calls:  Math.max(prev.calls  || 0, hist.calls  || 0),
            emails: Math.max(prev.emails || 0, (hist.notes || 0) + (hist.emails || 0)),
            notes:  Math.max(prev.notes  || 0, hist.notes  || 0),
            // Use max so manual local logging isn't overwritten when HubSpot
            // count is lower, but in practice HubSpot is the source of truth
            visits: Math.max(prev.visits || 0, visits || 0),
            quotes: Math.max(prev.quotes || 0, quotes || 0),
            sales:  Math.max(prev.sales  || 0, sales  || 0),
          }));
        } catch (e) { console.warn("Activity counts:", e.message); }
        setHsLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentUser.id, period, isAdmin, users]);

  // Admin: pull live HubSpot activity counts for every rep with a HubSpot
  // Owner ID. Runs whenever the team view is shown so the scoreboard always
  // reflects the latest activity rather than stale localStorage data.
  useEffect(() => {
    if (!isAdmin || view !== "team") return;
    const reps = (users || []).filter(u => !u.isAdmin && u.hubspotOwnerId);
    if (reps.length === 0) return;

    let cancelled = false;
    const fetchPeriod = period; // Capture so we tag writes correctly even if period changes mid-fetch
    setHsLoading(true);

    (async () => {
      // Fire all reps' metrics in parallel. Each rep needs 4 separate HubSpot
      // queries (activity counts + visits + quotes + sales), but they don't
      // block each other. For 8 reps that's ~32 requests; with internal
      // throttling on getActivityCounts (3 sub-requests with sleeps) we stay
      // safely under HubSpot's rate limits.
      const results = await Promise.all(
        reps.map(async (u) => {
          try {
            const [hist, visits, quotes, sales] = await Promise.all([
              HS.getActivityCounts(u.hubspotOwnerId, fetchPeriod),
              HS.getInPersonVisitCount(u.hubspotOwnerId, fetchPeriod),
              HS.getDealsCreatedCount(u.hubspotOwnerId, fetchPeriod),
              HS.getSalesGenerated(u.hubspotOwnerId, fetchPeriod),
            ]);
            return { uid: u.id, hist, visits, quotes, sales };
          } catch (e) {
            console.warn("Metrics for " + (u.name || u.email) + ":", e.message);
            return { uid: u.id, hist: null, visits: 0, quotes: 0, sales: 0 };
          }
        })
      );
      if (cancelled) return;

      // Only write to state if we're still on the same period we fetched for.
      // Without this check, a slow Month fetch could complete after the user
      // switched to Quarter and overwrite the Quarter data with Month numbers.
      setAllActuals(prev => {
        const next = {...prev};
        for (const {uid, hist, visits, quotes, sales} of results) {
          const local = next[uid] || {calls:0, visits:0, emails:0, quotes:0, sales:0};
          next[uid] = {
            ...local,
            // Use max so manual local logging isn't overwritten by HubSpot
            calls:  Math.max(local.calls  || 0, hist?.calls  || 0),
            emails: Math.max(local.emails || 0, (hist?.notes || 0) + (hist?.emails || 0)),
            notes:  Math.max(local.notes  || 0, hist?.notes  || 0),
            visits: Math.max(local.visits || 0, visits || 0),
            quotes: Math.max(local.quotes || 0, quotes || 0),
            sales:  Math.max(local.sales  || 0, sales  || 0),
          };
        }
        return next;
      });
      setAllActualsPeriod(fetchPeriod);
      setHsLoading(false);
    })();

    return () => { cancelled = true; };
  }, [isAdmin, view, period, users]);

  const logAct = (id, delta) => {
    const next = {...actuals, [id]: (actuals[id] || 0) + delta};
    setActuals(next);
    sSet("crm_actuals_" + currentUser.id + "_" + period, next);
  };
  const saveQuotas = () => {
    sSet("crm_global_quotas", quotas);
    // Push to Azure Blob so other devices/admins see the updated targets.
    // Fire-and-forget; UI feedback is the "Saved" pill.
    serverSave("crm_global_quotas", quotas).catch(e =>
      console.warn("Could not save quotas to server:", e.message)
    );
    setSaved(true); setTimeout(()=>setSaved(false), 2000);
    setEditMode(false);
  };
  const getAI = async () => {
    setAiL(true); setAiTip("");
    try {
      const s = QUOTA_METRICS.map(m => m.label + ": " + (actuals[m.id] || 0) + "/" + sq[m.id]).join(", ");
      const d = await aiCall("Sales coach for mattress company. Rep " + (currentUser.name || currentUser.email) + ", " + period + " stats: " + s + ". Give 2-3 sentence motivational tip and one action for today. Be warm.");
      const t = d.content?.find(b => b.type === "text")?.text || "";
      setAiTip(t);
    } catch { setAiTip("Could not load tip."); }
    setAiL(false);
  };

  if (!quotas || !actuals) return <div style={{padding:40,textAlign:"center",color:"var(--muted)"}}><Spin/></div>;

  // Scale the stored monthly quotas by the selected period. Edit mode still
  // edits the monthly base values (in `quotas`); everywhere else we display
  // and compute percentages against `sq` (scaled quotas).
  const sq = scaleQuotas(quotas, period);

  const ovPct = QUOTA_METRICS.reduce((s, m) =>
    s + (sq[m.id] > 0 ? Math.min(100, (actuals[m.id] || 0) / sq[m.id] * 100) : 100), 0
  ) / QUOTA_METRICS.length;
  const oc = qColor(ovPct);
  const salesUsers = (users || []).filter(u => !u.isAdmin);
  const periodLabel = period === "month" ? "Month" : period === "quarter" ? "Quarter" : "YTD";
  const periodMult = periodMultiplier(period);

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div style={{display:"flex",gap:4,background:"var(--warm1)",borderRadius:12,padding:4}}>
          {isAdmin && (
            <button onClick={()=>setView("team")} style={{padding:"7px 18px",borderRadius:9,border:"none",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",transition:"all .15s",background:view==="team"?"#fff":"transparent",color:view==="team"?"var(--brown)":"var(--muted)",boxShadow:view==="team"?"var(--sh)":"none"}}>👥 Team View</button>
          )}
          <button onClick={()=>setView("mine")} style={{padding:"7px 18px",borderRadius:9,border:"none",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",transition:"all .15s",background:view==="mine"?"#fff":"transparent",color:view==="mine"?"var(--brown)":"var(--muted)",boxShadow:view==="mine"?"var(--sh)":"none"}}>📊 My Progress</button>
        </div>
        <div style={{display:"flex",gap:6}}>
          {["month","quarter","ytd"].map(p => (
            <button key={p} onClick={()=>setPeriod(p)} style={{padding:"7px 16px",borderRadius:99,border:"1.5px solid "+(period===p?"var(--brown)":"var(--warm2)"),background:period===p?"var(--warm1)":"#fff",color:period===p?"var(--brown)":"var(--muted)",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",transition:"all .15s"}}>
              {p === "month" ? "Month" : p === "quarter" ? "Quarter" : "YTD"}
            </button>
          ))}
        </div>
      </div>

      {view === "team" && isAdmin && (
        <div>
          <div style={{background:"#fff",borderRadius:16,padding:"20px 24px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:17,fontWeight:600,color:"var(--dark)"}}>🎯 Quota Targets - {periodLabel}</div>
                <div style={{fontSize:12,color:"var(--muted)",marginTop:3}}>These goals apply to all sales reps</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {saved && <span style={{fontSize:13,color:"var(--green)",fontWeight:600}}>✓ Saved!</span>}
                {editMode
                  ? <><Btn onClick={saveQuotas} style={{fontSize:12,padding:"6px 16px"}}>Save Goals</Btn><Btn variant="ghost" onClick={()=>setEditMode(false)} style={{fontSize:12,padding:"6px 16px"}}>Cancel</Btn></>
                  : <Btn variant="soft" onClick={()=>setEditMode(true)} style={{fontSize:12,padding:"6px 16px"}}>✏️ Edit Targets</Btn>
                }
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12}}>
              {QUOTA_METRICS.map(m => {
                const fmt = v => m.isCurrency ? "$" + Number(v).toLocaleString() : v;
                return (
                  <div key={m.id} style={{padding:"14px 16px",borderRadius:12,background:"var(--warm1)",border:"1px solid var(--warm2)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                      <span style={{fontSize:20}}>{m.icon}</span>
                      <span style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{m.label}</span>
                    </div>
                    {editMode
                      ? <>
                          <input type="number" value={quotas[m.id] || 0} onChange={e=>setQuotas(q=>({...q, [m.id]: Number(e.target.value)}))}
                            style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1.5px solid var(--warm3)",background:"#fff",fontSize:14,fontWeight:700,color:"var(--brown)",outline:"none",textAlign:"center"}}/>
                          <div style={{fontSize:9,color:"var(--muted)",textAlign:"center",marginTop:3,fontStyle:"italic"}}>monthly base · edit applies to all periods</div>
                        </>
                      : <div style={{fontSize:22,fontWeight:700,fontFamily:"Barlow Condensed,sans-serif",color:"var(--brown)",textAlign:"center"}}>{fmt(sq[m.id] || 0)}</div>
                    }
                    {!editMode && (
                      <div style={{fontSize:10,color:"var(--muted)",textAlign:"center",marginTop:4}}>
                        {periodLabel} target
                        {periodMult > 1 && <span style={{marginLeft:4}}>· {fmt(quotas[m.id] || 0)}/mo × {periodMult}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{background:"#fff",borderRadius:16,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",overflow:"hidden"}}>
            <div style={{padding:"16px 24px",borderBottom:"1px solid var(--warm1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:17,fontWeight:600,color:"var(--dark)",display:"flex",alignItems:"center",gap:10}}>
                📋 Team Scoreboard
                {hsLoading && <span style={{fontFamily:"Barlow,sans-serif",fontSize:11,fontWeight:400,fontStyle:"italic",color:"var(--muted)"}}><Spin/> syncing from HubSpot...</span>}
              </div>
              <div style={{fontSize:12,color:"var(--muted)"}}>{salesUsers.length} rep{salesUsers.length !== 1 ? "s" : ""} · {periodLabel}</div>
            </div>
            {salesUsers.length === 0
              ? <div style={{padding:"40px 0",textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>No sales reps yet. Add users via Manage Users in the sidebar.</div>
              : (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:"var(--warm1)",borderBottom:"2px solid var(--warm2)"}}>
                        <th style={{textAlign:"left",padding:"12px 16px",fontWeight:600,color:"var(--muted)",fontSize:11,textTransform:"uppercase",letterSpacing:.5,whiteSpace:"nowrap"}}>Rep</th>
                        {QUOTA_METRICS.map(m => (
                          <th key={m.id} style={{textAlign:"center",padding:"12px 10px",fontWeight:600,color:"var(--muted)",fontSize:11,textTransform:"uppercase",letterSpacing:.5,whiteSpace:"nowrap"}}>{m.icon} {m.label}</th>
                        ))}
                        <th style={{textAlign:"center",padding:"12px 16px",fontWeight:600,color:"var(--muted)",fontSize:11,textTransform:"uppercase",letterSpacing:.5}}>Overall</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesUsers.map((u, ri) => {
                        const ua = allActuals[u.id] || {};
                        const op = QUOTA_METRICS.reduce((s, m) =>
                          s + (sq[m.id] > 0 ? Math.min(100, (ua[m.id] || 0) / sq[m.id] * 100) : 100), 0
                        ) / QUOTA_METRICS.length;
                        const oc2 = qColor(op);
                        return (
                          <tr key={u.id} style={{borderBottom:"1px solid var(--warm1)",background:ri%2===0?"#fff":"#fdfcfa"}}>
                            <td style={{padding:"14px 16px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:10}}>
                                <div style={{width:32,height:32,borderRadius:99,background:"linear-gradient(135deg,#b01020,#111111)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0}}>{(u.name || u.email)[0].toUpperCase()}</div>
                                <div>
                                  <div style={{fontWeight:600,color:"var(--dark)",fontSize:13}}>{u.name || u.email.split("@")[0]}</div>
                                  <div style={{fontSize:11,color:"var(--muted)"}}>{u.email}</div>
                                </div>
                              </div>
                            </td>
                            {QUOTA_METRICS.map(m => {
                              const val = ua[m.id] || 0;
                              const pct = sq[m.id] > 0 ? Math.min(100, Math.round(val / sq[m.id] * 100)) : 100;
                              const c = qColor(pct);
                              const fmt = v => m.isCurrency ? "$" + v.toLocaleString() : v.toLocaleString();
                              return (
                                <td key={m.id} style={{textAlign:"center",padding:"14px 10px"}}>
                                  <div style={{display:"inline-flex",flexDirection:"column",alignItems:"center",gap:4,minWidth:70}}>
                                    <div style={{width:"100%",height:5,borderRadius:99,background:"var(--warm2)",overflow:"hidden"}}>
                                      <div style={{height:"100%",width:pct+"%",borderRadius:99,background:c.bar,transition:"width .5s"}}/>
                                    </div>
                                    <span style={{fontSize:12,fontWeight:600,color:"var(--dark)"}}>{fmt(val)}</span>
                                    <span style={{fontSize:10,color:c.text,fontWeight:600,padding:"1px 7px",borderRadius:99,background:c.bg}}>{pct}%</span>
                                  </div>
                                </td>
                              );
                            })}
                            <td style={{textAlign:"center",padding:"14px 16px"}}>
                              <div style={{display:"inline-flex",flexDirection:"column",alignItems:"center",gap:6}}>
                                <div style={{width:48,height:48,borderRadius:99,background:oc2.bg,border:"3px solid "+oc2.bar,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                  <span style={{fontSize:14,fontWeight:700,color:oc2.text,fontFamily:"Barlow Condensed,sans-serif"}}>{Math.round(op)}%</span>
                                </div>
                                <span style={{fontSize:11,fontWeight:600,color:oc2.text}}>{oc2.label}</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </div>
      )}

      {view === "mine" && (
        <div>
          <div style={{display:"flex",gap:14,marginBottom:24,flexWrap:"wrap",alignItems:"stretch"}}>
            <div style={{flex:2,minWidth:200,background:"#fff",borderRadius:16,padding:"20px 24px",boxShadow:"var(--sh)",border:"1.5px solid "+oc.bar+"44"}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:6,display:"flex",alignItems:"center",gap:8}}>
                Overall Progress
                {hsLoading && <span style={{fontSize:11,color:"var(--muted)",fontWeight:400,fontStyle:"italic"}}><Spin/> syncing from HubSpot...</span>}
              </div>
              <div style={{fontSize:36,fontWeight:700,fontFamily:"Barlow Condensed,sans-serif",color:oc.bar}}>{Math.round(ovPct)}%</div>
              <div style={{fontSize:13,color:oc.text,marginTop:2}}>{oc.label} - {period === "month" ? "This Month" : period === "quarter" ? "This Quarter" : "YTD"}</div>
              <div style={{height:6,borderRadius:99,background:"var(--warm2)",marginTop:12,overflow:"hidden"}}>
                <div style={{height:"100%",width:ovPct+"%",borderRadius:99,background:oc.bar,transition:"width .6s"}}/>
              </div>
            </div>
          </div>
          <div style={{marginBottom:20}}>
            {QUOTA_METRICS.map(m => {
              const pct = sq[m.id] > 0 ? Math.min(100, Math.round((actuals[m.id] || 0) / sq[m.id] * 100)) : 0;
              const c = qColor(pct);
              const fmt = v => m.isCurrency ? "$" + v.toLocaleString() : v.toLocaleString();
              return (
                <div key={m.id} style={{position:"relative",padding:"14px 16px",borderRadius:14,background:c.bg,border:"1px solid "+c.bar+"33",marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:18}}>{m.icon}</span>
                      <span style={{fontWeight:600,fontSize:14,color:"var(--dark)"}}>{m.label}</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:12,fontWeight:700,color:c.text,padding:"2px 8px",borderRadius:99,background:c.bar+"22"}}>{c.label}</span>
                      <span style={{fontSize:13,fontWeight:600}}>{fmt(actuals[m.id] || 0)} <span style={{color:"var(--muted)",fontWeight:400}}>/ {fmt(sq[m.id] || 0)}</span></span>
                    </div>
                  </div>
                  <div style={{height:8,borderRadius:99,background:"rgba(0,0,0,.08)",overflow:"hidden"}}>
                    <div style={{height:"100%",width:pct+"%",borderRadius:99,background:c.bar,transition:"width .6s"}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:5,fontSize:11,color:c.text}}>
                    <span>{pct}% of {periodLabel} goal</span>
                    <span>{(sq[m.id] || 0) - (actuals[m.id] || 0) > 0 ? fmt((sq[m.id] || 0) - (actuals[m.id] || 0)) + " to go" : "Goal reached! 🎉"}</span>
                  </div>
                  <button onClick={()=>logAct(m.id, m.isCurrency ? 500 : 1)}
                    style={{position:"absolute",top:14,right:14,padding:"3px 10px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:11,color:"var(--brown)",cursor:"pointer",fontFamily:"Barlow,sans-serif",fontWeight:600}}>+ Log</button>
                </div>
              );
            })}
          </div>
          <div style={{background:"#fff",borderRadius:16,padding:"18px 20px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:14,fontWeight:600,color:"var(--dark)"}}>✦ AI Sales Coach</div>
              <Btn variant="soft" onClick={getAI} disabled={aiL} style={{fontSize:12,padding:"6px 14px"}}>{aiL ? <><Spin/> Thinking...</> : "Get Today's Tip"}</Btn>
            </div>
            {aiTip
              ? <p style={{fontSize:13,color:"var(--text)",lineHeight:1.7,fontStyle:"italic"}}>"{aiTip}"</p>
              : <p style={{fontSize:13,color:"var(--muted)",fontStyle:"italic"}}>Click "Get Today's Tip" for a personalized coaching suggestion.</p>
            }
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminQuotaView({users, onClose}) {
  const [quotas,  setQuotas]  = useState({...DEFAULT_QUOTAS});
  const [actuals, setActuals] = useState({});
  const [period,  setPeriod]  = useState("month");
  const [saved,   setSaved]   = useState(false);

  useEffect(() => {
    // Same server-first load as QuotaDashboard so edits sync across devices.
    const local = sGet("crm_global_quotas") || DEFAULT_QUOTAS;
    setQuotas({...DEFAULT_QUOTAS, ...local});
    serverLoad("crm_global_quotas").then(srv => {
      if (srv && typeof srv === "object") {
        setQuotas({...DEFAULT_QUOTAS, ...srv});
        sSet("crm_global_quotas", srv);
      }
    }).catch(()=>{});
    const all = {};
    for (const u of users.filter(u => !u.isAdmin)) {
      const a = sGet("crm_actuals_" + u.id + "_" + period) || {};
      all[u.id] = {calls:a.calls||0, visits:a.visits||0, emails:a.emails||0, quotes:a.quotes||0, sales:a.sales||0};
    }
    setActuals(all);
  }, [period, users]);

  const save = () => {
    sSet("crm_global_quotas", quotas);
    serverSave("crm_global_quotas", quotas).catch(e =>
      console.warn("Could not save quotas to server:", e.message)
    );
    setSaved(true); setTimeout(()=>setSaved(false), 2000);
  };
  const salesUsers = users.filter(u => !u.isAdmin);
  // Scale the monthly quotas by the selected period for leaderboard math
  const sq = scaleQuotas(quotas, period);
  const periodMult = periodMultiplier(period);

  return (
    <Modal title="🎯  Quota Management" onClose={onClose} wide>
      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {["month","quarter","ytd"].map(p => (
          <button key={p} onClick={()=>setPeriod(p)} style={{padding:"6px 14px",borderRadius:99,border:"1.5px solid "+(period===p?"var(--brown)":"var(--warm2)"),background:period===p?"var(--warm1)":"#fff",color:period===p?"var(--brown)":"var(--muted)",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>
            {p === "month" ? "This Month" : p === "quarter" ? "This Quarter" : "YTD"}
          </button>
        ))}
      </div>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:12}}>
          Global Goals
          <span style={{marginLeft:6,textTransform:"none",letterSpacing:0,fontWeight:400,fontStyle:"italic"}}>(monthly base · scales × {periodMult} for {period === "month" ? "month" : period === "quarter" ? "quarter" : "YTD"})</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {QUOTA_METRICS.map(m => (
            <div key={m.id} style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:12,color:"var(--muted)",fontWeight:600}}>{m.icon} {m.label}</label>
              <input type="number" value={quotas[m.id] || 0} onChange={e=>setQuotas(q=>({...q, [m.id]: Number(e.target.value)}))}
                style={{padding:"8px 12px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,outline:"none"}}
                onFocus={e=>e.target.style.borderColor="var(--warm3)"}
                onBlur={e=>e.target.style.borderColor="var(--warm2)"}/>
            </div>
          ))}
        </div>
        <div style={{marginTop:12,display:"flex",gap:10,alignItems:"center"}}>
          <Btn onClick={save}>Save Goals</Btn>
          {saved && <span style={{fontSize:13,color:"var(--green)",fontWeight:600}}>✓ Saved!</span>}
        </div>
      </div>
      {salesUsers.length > 0 && (
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:12}}>Team Scoreboard</div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:"2px solid var(--warm2)"}}>
                  <th style={{textAlign:"left",padding:"8px 10px",color:"var(--muted)",fontWeight:600}}>Rep</th>
                  {QUOTA_METRICS.map(m => <th key={m.id} style={{textAlign:"center",padding:"8px 10px",color:"var(--muted)",fontWeight:600,whiteSpace:"nowrap"}}>{m.icon} {m.label}</th>)}
                  <th style={{textAlign:"center",padding:"8px 10px",color:"var(--muted)",fontWeight:600}}>Overall</th>
                </tr>
              </thead>
              <tbody>
                {salesUsers.map(u => {
                  const ua = actuals[u.id] || {};
                  const op = QUOTA_METRICS.reduce((s, m) =>
                    s + (sq[m.id] > 0 ? Math.min(100, (ua[m.id] || 0) / sq[m.id] * 100) : 100), 0
                  ) / QUOTA_METRICS.length;
                  const oc = qColor(op);
                  return (
                    <tr key={u.id} style={{borderBottom:"1px solid var(--warm1)"}}>
                      <td style={{padding:"10px",fontWeight:600,color:"var(--dark)"}}>{u.name || u.email}</td>
                      {QUOTA_METRICS.map(m => {
                        const pct = sq[m.id] > 0 ? Math.min(100, Math.round((ua[m.id] || 0) / sq[m.id] * 100)) : 100;
                        const c = qColor(pct);
                        return (
                          <td key={m.id} style={{textAlign:"center",padding:"10px"}}>
                            <span style={{fontSize:11,fontWeight:700,color:c.text,padding:"2px 7px",borderRadius:99,background:c.bg,border:"1px solid "+c.bar+"44"}}>{pct}%</span>
                          </td>
                        );
                      })}
                      <td style={{textAlign:"center",padding:"10px"}}>
                        <span style={{fontSize:12,fontWeight:700,color:oc.text,padding:"3px 10px",borderRadius:99,background:oc.bg,border:"1px solid "+oc.bar+"44"}}>{Math.round(op)}% - {oc.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {salesUsers.length === 0 && <p style={{fontSize:13,color:"var(--muted)",textAlign:"center",padding:"20px 0",fontStyle:"italic"}}>No sales reps yet.</p>}
    </Modal>
  );
}
